// ShiftSettings.tsx
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  activeInactiveShiftSetting,
  deleteShiftSetting,
  getAllShiftSettings,
} from "./services/shiftSettings.service";
import { clearShiftSettingsID, setShiftSettingsID } from "./shiftSettingsSlice";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import LinkEdit from "../../components/drawer/LinkEdit";

const ShiftSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllShiftSettings());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteShiftSettingDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Shift Setting List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate
              label="Add New Shift Setting"
              drawerName="shiftSettingsForm"
            />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <ShiftSettingsGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const ShiftSettingsGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const ShiftSettingsList = useAppSelector(
    (state) => state.shiftSettings.ShiftSettingsList,
  );
  const loading = useAppSelector((state) => state.shiftSettings.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? ShiftSettingsList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteShiftSetting"));
      dispatch(setShiftSettingsID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.tdProps.style,
        }}
      >
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="shiftSettingsForm"
            tooltipTitle="Edit"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 6 || props.index === 7 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(activeInactiveShiftSetting(data)).then(() => {
        dispatch(getAllShiftSettings());
      });
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.tdProps.style,
        }}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  const ApplicableDaysCell = (props: GridCellProps) => {
    const days = props.dataItem?.ApplicableDays;
    if (!days) return <td>-</td>;

    const dayList = typeof days === "string" ? days.split(",") : days;
    const displayText =
      dayList.length > 2
        ? `${dayList.slice(0, 2).join(", ")} +${dayList.length - 2}`
        : dayList.join(", ");

    return (
      <td
        style={{ ...props.style }}
        className={props.className}
        title={Array.isArray(dayList) ? dayList.join(", ") : days}
      >
        {displayText}
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {ShiftSettingsList && ShiftSettingsList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(ShiftSettingsList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : ShiftSettingsList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={ShiftSettingsList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, 20, 25, 50, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="ShiftName"
            title="Shift Name"
            width={"250px"}
            cells={{
              data: (props: any) => (
                <td
                  className={`${isEditAccess ? "hoverableName" : "normalName"}`}
                >
                  {isEditAccess ? (
                    <LinkEdit
                      ID={props.dataItem.ID}
                      drawerName="shiftSettingsForm"
                      label={props.dataItem?.ShiftName}
                    />
                  ) : (
                    props.dataItem?.ShiftName
                  )}
                </td>
              ),
            }}
          />
          <Column field="DoctorName" title="Doctor" width={200} />
          {/* <Column field="BranchName" title="Branch" width={"150px"} /> */}
          <Column
            field="StartTime"
            title="Start Time"
            width={"150px"}
            // cells={{
            //   data: (props: any) => (
            //     <td>
            //       {props.dataItem.StartTime
            //         ? moment(props.dataItem.StartTime, "HH:mm").format(
            //             "hh:mm A",
            //           )
            //         : ""}
            //     </td>
            //   ),
            // }}
          />
          <Column
            field="EndTime"
            title="End Time"
            width={"150px"}
            // cells={{
            //   data: (props: any) => (
            //     <td>
            //       {props.dataItem.EndTime
            //         ? moment(props.dataItem.EndTime, "HH:mm").format("hh:mm A")
            //         : ""}
            //     </td>
            //   ),
            // }}
          />
          <Column
            field="SlotDuration"
            title="Slot Duration"
            width={"150px"}
            cells={{
              data: (props: any) => <td>{props.dataItem.SlotDuration}min</td>,
            }}
          />
          <Column
            field="ApplicableDays"
            title="Applicable Days"
            width={"180px"}
            cell={ApplicableDaysCell}
          />
          <Column
            field="IsActive"
            title="Status"
            width={"90px"}
            cells={{ data: MyActiveInActiveCommandCell }}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              width={"110px"}
              cells={{ data: MyEditCommandCell }}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteShiftSettingDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.shiftSettings.loading);
  const ShiftSettingsID = useAppSelector(
    (state) => state.shiftSettings.ShiftSettingsID,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteShiftSetting(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(getAllShiftSettings());
        dispatch(closeDialog());
        dispatch(clearShiftSettingsID());
      } else {
        dispatch(getAllShiftSettings());
        dispatch(closeDialog());
        dispatch(clearShiftSettingsID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteShiftSetting" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Shift Setting</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this shift setting?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(ShiftSettingsID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default ShiftSettings;
